import { createQueryController } from '@tanstack/lit-query';
import { trackVideoSlidesEvent, VideoSlidesEvents } from 'Analytics/video-slides-events';
import { queryClient } from 'Api/query-client';
import { slidesQueryOptions } from 'Api/videos';
import type { Contributor } from 'Components/vm-video-card/vm-video-card';
import { html, LitElement, nothing } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import 'Modules/video/components/vm-video-slides-viewer/vm-video-slides-viewer';
import type { SlideChangeDetail } from 'Modules/video/components/vm-video-slides-viewer/vm-video-slides-viewer';
import { getInterstitialStates } from 'Utils/dynamic-state';

export type SlidesOpenSource = 'video_page' | 'deeplink';
/** `dismiss` covers the close button, Escape and backdrop. */
export type SlidesCloseMethod = 'dismiss' | 'watch_full_video' | 'back';

export interface SlidesOpenDetail {
    source: SlidesOpenSource;
    slideCount: number;
}

/** Slide numbers are 1-based positions in the deck. */
export interface SlidesCloseDetail {
    method: SlidesCloseMethod;
    durationMs: number;
    /** Unique slides shown, including the first. */
    slidesViewed: number;
    maxSlideNumber: number;
    lastSlideNumber: number;
}

const DEEP_LINK_PARAM = 'view';
const DEEP_LINK_VALUE = 'slides';
const HISTORY_STATE_KEY = 'vmSlides';

/** Every opening starts on the first slide. */
const newSession = () => ({ openedAt: performance.now(), viewed: new Set([1]), last: 1 });

const isOwnHistoryEntry = (): boolean => window.history.state?.[HISTORY_STATE_KEY] === true;

/** undefined | true (shown) | false (not in way) */
const appGateState = () => getInterstitialStates().find(({ uid }) => uid === 'vm-app-gate')?.data;

/** Resolves once the iOS app gate is dismissed or not shown. */
function whenAppGateResolved(): Promise<void> {
    return new Promise((resolve) => {
        const check = () => {
            if (appGateState() !== false) return false;
            window.removeEventListener('dynamicstatechange', check);
            resolve();
            return true;
        };
        if (!check()) window.addEventListener('dynamicstatechange', check);
    });
}

/** Removes `?view=slides` from the URL, keeping other params. */
function removeDeepLinkParam(): void {
    const url = new URL(window.location.href);
    url.searchParams.delete(DEEP_LINK_PARAM);
    window.history.replaceState(window.history.state, '', url);
}

/**
 * Video slides.
 *
 * Handles:
 * - slides viewer toggle on "View slides" tap (see vm-video-slides-button) and query param directive (?view=slides),
 * - slides fetch from backend API,
 * - slide navigation analytics; open/close analytics need the player, see `slides-player-coordinator`.
 *
 * The page reads `deepLinked` property to keep the video from autoplaying.
 *
 * States:
 * ```
 *                                                                                  ┌────> (push history)
 * [closed] ─┬─ button ────────────────────────┬─> [loading] ─ slides loaded ─> [visible] ─ close / Back ─> [closed]
 *           └─ deep link ─> [app gate wait] ──┘                            (pop history) <───┘
 * ```
 * `loading` is skipped when slides are cached. Entering `visible` fires `open`, leaving it fires `close`.
 * `visible` adds a history entry, Back closes the viewer. Any other close pops history.
 *
 * @fires vm-video-slides:open - Detail: `SlidesOpenDetail`.
 * @fires vm-video-slides:close - Detail: `SlidesCloseDetail`.
 *
 * @attr video-slug - Slug used to fetch slides.
 * @attr video-title, publisher, published-at, video-thumbnail-url -
 *   Video card metadata, forwarded to the viewer.
 */
@customElement('vm-video-slides')
export class VmVideoSlides extends LitElement {
    @property({ type: String, attribute: 'video-slug' })
    videoSlug = '';

    @property({ type: String, attribute: 'video-title' })
    videoTitle = '';

    /** JSON `{name, href?}` object. */
    @property({ type: Object })
    publisher: Contributor | null = null;

    @property({ type: String, attribute: 'published-at' })
    publishedAt = '';

    @property({ type: String, attribute: 'video-thumbnail-url' })
    videoThumbnailUrl = '';

    /** Page was opened with `?view=slides`; this is read in index.js on DOMContentLoaded */
    readonly deepLinked = new URLSearchParams(window.location.search).get(DEEP_LINK_PARAM) === DEEP_LINK_VALUE;

    private _deepLinkHandled = false;

    /** 2 ways to open slides: via button or deep link */
    private _source: SlidesOpenSource = 'video_page';

    private _closeMethod: SlidesCloseMethod = 'dismiss';

    private _visible = false;

    /** Set from our `history.back()` until its popstate arrives. */
    private _backPending = false;

    /** The current opening, for the close summary. */
    private _session = newSession();

    @state()
    private _requested = false;

    /** Reflected so page code can query `vm-video-slides[open]`, e.g. the brand-alert blockers. */
    @property({ type: Boolean, reflect: true })
    open = false;

    private _slides = createQueryController(
        this,
        () => slidesQueryOptions(this._requested ? this.videoSlug : undefined),
        queryClient,
    );

    private _onRequestOpen = (e: HTMLElementEventMap['vm-video-slides:request-open']) => {
        if (e.defaultPrevented) return;
        this._show('video_page');
    };

    private _show(source: SlidesOpenSource): void {
        this._source = source;
        this._requested = true;
        this.open = true;
    }

    override connectedCallback(): void {
        super.connectedCallback();
        document.addEventListener<any>('vm-video-slides:request-open', this._onRequestOpen);
        window.addEventListener('popstate', this._onPopState);

        if (this.deepLinked && !this._deepLinkHandled) {
            this._deepLinkHandled = true;
            void this._openFromDeepLink();
        }
    }

    private async _openFromDeepLink(): Promise<void> {
        // Prefetch behind the app gate.
        this._requested = true;
        await whenAppGateResolved();

        if (!this.isConnected) return;
        // Kept until now for the app gate's "Open in app" link; removed so a reload doesn't reopen.
        removeDeepLinkParam();
        this._show('deeplink');
    }

    override disconnectedCallback() {
        document.removeEventListener<any>('vm-video-slides:request-open', this._onRequestOpen);
        window.removeEventListener('popstate', this._onPopState);
        super.disconnectedCallback();
    }

    private _onPopState = () => {
        if (this._backPending) {
            this._backPending = false;
            // Reopened before our `history.back()` landed.
            if (this._visible) this._pushHistoryEntry();
            return;
        }
        if (this._visible && !isOwnHistoryEntry()) {
            this._closeMethod = 'back';
            this.open = false;
        }
    };

    private _pushHistoryEntry(): void {
        window.history.pushState({ [HISTORY_STATE_KEY]: true }, '');
    }

    private _popHistoryEntry(): void {
        // A pending back already pops our entry; another would leave the page.
        if (this._backPending || !isOwnHistoryEntry()) return;
        this._backPending = true;
        window.history.back();
    }

    private _onSlideChange({ detail }: CustomEvent<SlideChangeDetail>) {
        const to = detail.toIndex + 1;
        this._session.viewed.add(to);
        this._session.last = to;
        trackVideoSlidesEvent(VideoSlidesEvents.PAGE_NAVIGATION, {
            method: detail.method,
            from_slide: detail.fromIndex + 1,
            to_slide: to,
            slide_id: detail.slideId,
        });
    }

    private _onWatchVideo() {
        this._closeMethod = 'watch_full_video';
    }

    private _onClose() {
        this.open = false;
    }

    private get _slidesData() {
        const { isError, data } = this._slides.current;
        return isError ? undefined : data;
    }

    override updated(): void {
        // `open` is set before the slides load; the viewer only shows once they have.
        const visible = this.open && !!this._slidesData;
        if (visible === this._visible) return;
        this._visible = visible;

        if (visible) {
            this._closeMethod = 'dismiss';
            this._session = newSession();
            if (!this._backPending) this._pushHistoryEntry();
            this._emit<SlidesOpenDetail>('vm-video-slides:open', {
                source: this._source,
                slideCount: this._slidesData?.slides.length ?? 0,
            });
        } else {
            if (this._closeMethod !== 'back') this._popHistoryEntry();
            const { openedAt, viewed, last } = this._session;
            this._emit<SlidesCloseDetail>('vm-video-slides:close', {
                method: this._closeMethod,
                durationMs: Math.round(performance.now() - openedAt),
                slidesViewed: viewed.size,
                maxSlideNumber: Math.max(...viewed),
                lastSlideNumber: last,
            });
        }
    }

    private _emit<T>(type: string, detail: T): void {
        this.dispatchEvent(new CustomEvent<T>(type, { detail, bubbles: true, composed: true }));
    }

    override render() {
        const data = this._slidesData;

        if (!data) {
            return nothing;
        }

        const { slides } = data;

        return html`
            <vm-video-slides-viewer
                ?open=${this.open}
                .slides=${slides}
                video-title=${this.videoTitle}
                .publisher=${this.publisher}
                published-at=${this.publishedAt}
                video-thumbnail-url=${this.videoThumbnailUrl}
                @vm-video-slides-viewer:change=${this._onSlideChange}
                @vm-video-slides-viewer:watch-video=${this._onWatchVideo}
                @vm-close="${this._onClose}"
            ></vm-video-slides-viewer>
        `;
    }
}

declare global {
    interface HTMLElementTagNameMap {
        'vm-video-slides': VmVideoSlides;
    }

    interface HTMLElementEventMap {
        'vm-video-slides:request-open': CustomEvent;
        'vm-video-slides:open': CustomEvent<SlidesOpenDetail>;
        'vm-video-slides:close': CustomEvent<SlidesCloseDetail>;
    }

    interface DocumentEventMap {
        'vm-video-slides:open': CustomEvent<SlidesOpenDetail>;
        'vm-video-slides:close': CustomEvent<SlidesCloseDetail>;
    }
}
